import {
  ChainId,
  CurrencyAmount,
  NativeCurrency,
  Percent,
  Token,
  TradeType,
  V3_CORE_FACTORY_ADDRESSES,
  V2_ROUTER_ADDRESS, MULTICALL_ADDRESSES
} from "@uniswap/sdk-core";
import {AlphaRouter, SwapOptionsSwapRouter02, SwapRoute, SwapType} from "@uniswap/smart-order-router";
import { StaticJsonRpcProvider } from '@ethersproject/providers'
import {HarmonyChainId} from "../constants";
import JSBI from "jsbi";
import {QuoteState} from "../types";
import {transformSwapRouteToGetQuoteResult} from "../utils/transformSwapRoute";
import {ethers} from "ethers";
import {computePoolAddress, FeeAmount, Pool, Route, SwapOptions, SwapQuoter, Trade, toHex} from "@uniswap/v3-sdk";
import IUniswapV3PoolABI from '@uniswap/v3-core/artifacts/contracts/interfaces/IUniswapV3Pool.sol/IUniswapV3Pool.json'
import QuoterJson from '@uniswap/v3-periphery/artifacts/contracts/lens/Quoter.sol/Quoter.json'
import { QUOTER_ADDRESSES } from '@uniswap/sdk-core'
import isZero, {fromReadableAmount} from "../utils";
import {Currency} from "@uniswap/sdk-core/dist/entities/currency";
import { SwapRouter, UNIVERSAL_ROUTER_ADDRESS } from '@uniswap/universal-router-sdk'
import {SwapOptionsUniversalRouter} from "@uniswap/smart-order-router/build/main/routers/router";
import {HarmonyNativeCurrency} from "../tokens";
const { abi: QuoterABI } = QuoterJson

export type TokenTrade = Trade<Token, Token, TradeType>;

const QUOTER_CONTRACT_ADDRESS = QUOTER_ADDRESSES[ChainId.HARMONY]
export const DEFAULT_MAX_FEE_PER_GAS = 10000000000; // 10 gwei
export const DEFAULT_MAX_PRIORITY_FEE_PER_GAS = 10000000000; // 10 gwei
export const DEFAULT_TOKEN_APPROVAL_AMOUNT = 1000; // whole units

export interface BaseTradeConfig {
  wallet: {
    address: string
  },
  tokens: {
    in: Currency;
    amountIn: number;
    out: Currency;
    poolFee: number;
  };
}


const routers = new Map<ChainId, AlphaRouter>()
const harmonyProvider = new StaticJsonRpcProvider('https://api.harmony.one')
export const harmonyRouter = getRouter(HarmonyChainId)


export function getRouter(chainId: ChainId | number): AlphaRouter {
  let router = routers.get(chainId)
  if (router) return router

  const provider = harmonyProvider
  router = new AlphaRouter({
    chainId,
    provider,
  })
  routers.set(chainId, router)
  return router
}

export const quoteTrade = async (
  currencyIn: Token | NativeCurrency,
  currencyOut: Token | NativeCurrency,
  amountRaw: string,
  tradeType: TradeType
) => {
  const baseCurrency = tradeType === TradeType.EXACT_INPUT ? currencyIn : currencyOut
  const quoteCurrency = tradeType === TradeType.EXACT_INPUT ? currencyOut : currencyIn
  const amount = CurrencyAmount.fromRawAmount(baseCurrency, JSBI.BigInt(amountRaw))

  const routerConfig: any = { protocols: ["V3"] }
  // console.log('swapRoute params:', 'tradeType', tradeType, 'amountRaw', amountRaw, 'quoteCurrency', quoteCurrency, 'tradeType', tradeType, 'routerConfig', routerConfig)
  const swapRoute = await harmonyRouter.route(amount, quoteCurrency, tradeType, /*swapConfig=*/ undefined, routerConfig)
  if (!swapRoute) {
    return { state: QuoteState.NOT_FOUND }
  }

  return transformSwapRouteToGetQuoteResult(tradeType, amount, swapRoute)
}

export const getSwapQuote = async (tokenIn: Token, tokenOut: Token, amountIn: number) => {
  const factoryAddress = V3_CORE_FACTORY_ADDRESSES[ChainId.HARMONY]
  const currentPoolAddress = computePoolAddress({
    factoryAddress: factoryAddress,
    tokenA: tokenIn,
    tokenB: tokenOut,
    fee: FeeAmount.MEDIUM,
  })

  const poolContract = new ethers.Contract(
    currentPoolAddress,
    IUniswapV3PoolABI.abi,
    harmonyProvider
  )

  const [token0, token1, fee, liquidity, slot0] = await Promise.all([
    poolContract.token0(),
    poolContract.token1(),
    poolContract.fee(),
    poolContract.liquidity(),
    poolContract.slot0(),
  ])

  const quoterContract = new ethers.Contract(
    QUOTER_CONTRACT_ADDRESS,
    QuoterABI,
    harmonyProvider
  )

  // console.log('quoterContract:', quoterContract)
  const amount = fromReadableAmount(
    1_000_000,
    tokenIn.decimals
  ).toString()
  console.log('token0', token0, 'token1', token1, 'fee', fee, 'amount', amount)

  // const quotedAmountOut = await quoterContract.callStatic.quoteExactInputSingle(
  //   token0,
  //   token1,
  //   fee,
  //   1,
  //   0
  // )

  const quotedAmountOut = 0

  return {
    quotedAmountOut
  }
}

// export const getV3PoolInfo = async (config: BaseTradeConfig) => {
//   const factoryAddress = V3_CORE_FACTORY_ADDRESSES[ChainId.HARMONY]
//   const currentPoolAddress = computePoolAddress({
//     factoryAddress: factoryAddress,
//     tokenA: config.tokens.in,
//     tokenB: config.tokens.out,
//     fee: config.tokens.poolFee,
//   })
//
//   const poolContract = new ethers.Contract(
//     currentPoolAddress,
//     IUniswapV3PoolABI.abi,
//     harmonyProvider
//   )
//
//   const [token0, token1, fee, liquidity, slot0] =
//     await Promise.all([
//       poolContract.token0(),
//       poolContract.token1(),
//       poolContract.fee(),
//       poolContract.liquidity(),
//       poolContract.slot0(),
//     ])
//
//   return {
//     fee,
//     liquidity,
//     sqrtPriceX96: slot0[0],
//     tick: slot0[1],
//   }
// }

async function getOutputQuote(route: Route<Currency, Currency>, config: BaseTradeConfig) {
  const { calldata } = await SwapQuoter.quoteCallParameters(
    route,
    CurrencyAmount.fromRawAmount(
      config.tokens.in,
      fromReadableAmount(
        config.tokens.amountIn,
        config.tokens.in.decimals
      ).toString()
    ),
    TradeType.EXACT_INPUT,
    {
      useQuoterV2: true,
    }
  );

  const quoteCallReturnData = await harmonyProvider.call({
    to: QUOTER_CONTRACT_ADDRESS,
    data: calldata,
  });

  const abiCoder = new ethers.utils.AbiCoder()
  return abiCoder.decode(
    ["uint256"],
    quoteCallReturnData
  );
}

export const ERC20_ABI = [
  // Read-Only Functions
  "function balanceOf(address owner) view returns (uint256)",
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)",

  // Authenticated Functions
  "function transfer(address to, uint amount) returns (bool)",
  "function approve(address _spender, uint256 _value) returns (bool)",

  // Events
  "event Transfer(address indexed from, address indexed to, uint amount)",
];

// export const createV3Trade = async (config: BaseTradeConfig) => {
//   const inputToken = config.tokens.in;
//   const inputAmount = fromReadableAmount(
//     config.tokens.amountIn,
//     inputToken.decimals
//   );
//   const poolInfo = await getV3PoolInfo(config)
//
//   const poolFee = FeeAmount.MEDIUM
//   const pool = new Pool(
//     config.tokens.in,
//     config.tokens.out,
//     poolFee,
//     poolInfo.sqrtPriceX96.toString(),
//     poolInfo.liquidity.toString(),
//     poolInfo.tick
//   )
//
//   const swapRoute = new Route(
//     [pool],
//     config.tokens.in,
//     config.tokens.out,
//   )
//
//   const amountOut = await getOutputQuote(swapRoute, config);
//
//   const uncheckedTrade = Trade.createUncheckedTrade({
//     route: swapRoute,
//     inputAmount: CurrencyAmount.fromRawAmount(
//       inputToken,
//       inputAmount.toString()
//     ),
//     outputAmount: CurrencyAmount.fromRawAmount(
//       config.tokens.out,
//       JSBI.BigInt(amountOut)
//     ),
//     tradeType: TradeType.EXACT_INPUT,
//   });
//
//   return uncheckedTrade;
// }

// export const executeTrade = async (trade: TokenTrade, address: string) => {
//   const options: SwapOptions = {
//     slippageTolerance: new Percent(50, 10_000), // 50 bips, or 0.50%
//     deadline: Math.floor(Date.now() / 1000) + 60 * 20, // 20 minutes from the current Unix time
//     recipient: address, // specifying a recipient is neat
//   };
//   // @ts-ignore
//   const { calldata: data, value } = SwapRouter.swapERC20CallParameters(trade, {
//     slippageTolerance: options.slippageTolerance,
//     deadlineOrPreviousBlockhash: options.deadline?.toString(),
//     inputTokenPermit: undefined,
//     fee: undefined
//   })
//   const tx = {
//     from: address,
//     to: UNIVERSAL_ROUTER_ADDRESS(ChainId.HARMONY),
//     data,
//     // TODO(https://github.com/Uniswap/universal-router-sdk/issues/113): universal-router-sdk returns a non-hexlified value.
//     ...(value && !isZero(value) ? { value: toHex(value) } : {}),
//   }
//   return tx
// }

export async function generateRoute(config: BaseTradeConfig): Promise<SwapRoute | null> {
  const router = new AlphaRouter({
    chainId: ChainId.HARMONY,
    provider: harmonyProvider,
  })

  const options: SwapOptionsUniversalRouter = {
    recipient: config.wallet.address,
    slippageTolerance: new Percent(50, 10_000),
    // deadline: Math.floor(Date.now() / 1000 + 1800),
    type: SwapType.UNIVERSAL_ROUTER,
  }

  const route = await router.route(
    CurrencyAmount.fromRawAmount(
      config.tokens.in,
      fromReadableAmount(
        config.tokens.amountIn,
        config.tokens.in.decimals
      ).toString()
    ),
    config.tokens.out,
    TradeType.EXACT_INPUT,
    options
  )

  return route
}
